import { Dropdown, GenericPanelWithDropdown, InfoRow, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { MeetingOutputDTO, MeetingState, RoomOutputDTO, TimeEventOutputDTO, UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { isArray } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExpectedRoleInfo, getExpectedRoleInfo } from "../../helpers/meeting";
import { useSettings } from "../../hooks/settings";

import styles from "./TimeEventsAgenda.module.css";
import clsx from "clsx";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { RoleBadge } from "../common/RoleBadge";

/**
 * What the panel is listing. Drives the row's action button and the default subtitle:
 * `class` joins an ongoing event, `replay` watches a recording, `instant` reopens a meeting.
 */
export type AgendaMode = "class" | "replay" | "instant";

export interface TimeEventsAgendaProps {
  timeEvents?: TimeEventOutputDTO | TimeEventOutputDTO[];
  placeholdersNb: number;
  selectedEventId?: string;
  kind?: "student" | "expert" | "pedagogy";
  mode?: AgendaMode;
  onSelectEvent?: (eventId: string) => void;
  onJoinEvent?: (eventId: string) => void;
  user?: UserOutputDTO;
}

export interface EventData {
  id: string;
  title: string;
  /** Room and sub-organization ids feed the panel filters; the names are what they display. */
  roomId?: string;
  roomName?: string;
  subOrganizationId?: string;
  subOrganizationName?: string;
  /** Small line above the title. Defaults to the sub-organization and room names. */
  header?: string;
  startDatetime: Date;
  /** Absent when the entry has no planned range — a meeting has a creation date, not a slot. */
  endDatetime?: Date;
  eventType?: string;
  expertName?: string;
  promotion?: string;
  roleInfo?: ExpectedRoleInfo;
  /** Skips the time-window computation: use it when the real status is not a matter of dates. */
  status?: EventStatus;
  /** Replaces the default "Évènement ..." text. */
  statusLabel?: React.ReactNode;
  /** Replaces the default action button label. */
  actionLabel?: string;
}

export type EventStatus = "incoming" | "ongoing" | "finished";

const getEventStatus = (event: EventData): EventStatus => {
  if (event.status) return event.status;
  const now = new Date();
  if (event.startDatetime > now) return "incoming";
  if (event.endDatetime && event.endDatetime < now) return "finished";
  return "ongoing";
}

const MEETING_STATE_LABELS: { [key in MeetingState]: string } = {
  [MeetingState.IDLE]: "En attente",
  [MeetingState.RUNNING]: "En cours",
  [MeetingState.PAUSED]: "En pause",
  [MeetingState.STOPPED]: "Terminé",
};

/** True while the meeting itself runs, whatever its planned slot says. */
export const isMeetingLive = (state?: MeetingState) => (
  state === MeetingState.RUNNING || state === MeetingState.PAUSED
);

/**
 * Status line read from the meeting's own state rather than from a time window. A running meeting
 * gets the live treatment: brighter text, a red dot and a cycling ellipsis. Returns `undefined`
 * for an unknown state, which leaves the default "Évènement ..." wording in place.
 */
export const getMeetingStatusLabel = (state?: MeetingState): React.ReactNode => {
  const label = state && MEETING_STATE_LABELS[state];
  if (!label) return undefined;
  if (state !== MeetingState.RUNNING) return label;
  return (
    <span className={styles.statusLive}>
      <span className={styles.liveDot} />
      {label}
      <span className={styles.ellipsis} aria-hidden="true">
        <span>.</span>
        <span>.</span>
        <span>.</span>
      </span>
    </span>
  );
};

export const convertTimeEvent = (
  event: TimeEventOutputDTO,
  user?: UserOutputDTO,
): EventData | undefined => {
  const meeting = event.target as MeetingOutputDTO;
  if (!meeting) return undefined;
  const expert = meeting.actors?.find((a) => a.externalProvider === "escaet:expert");
  const room = event.room as RoomOutputDTO; // populated room

  const roleInfo = user ? getExpectedRoleInfo(meeting, user._id, user.role) : undefined;
  const state = meeting.state;

  return {
    id: event._id,
    title: meeting.name || "",
    roomId: room?._id,
    roomName: room?.name,
    subOrganizationId: room?.subOrganization?._id,
    subOrganizationName: room?.subOrganization?.name,
    startDatetime: new Date(event.startDatetime),
    endDatetime: new Date(event.endDatetime),
    eventType: "cours",
    expertName: expert?.identity?.username ?? expert?.user?.identity?.username ?? "",
    promotion: (meeting as any).promos?.join(' '),
    roleInfo,
    // A live meeting is ongoing whatever its slot says; otherwise the slot decides, so a class
    // nobody started yet still offers its join button for the whole planned window.
    status: isMeetingLive(state) ? "ongoing" : undefined,
    statusLabel: getMeetingStatusLabel(state),
  };
}

const SortMethods: Record<string, string> = {
  date: "Trier par date",
  name: "Trier par nom",
  room: "Trier par salle",
};

/** Filter value that keeps every entry. Not empty: the dropdown shows its placeholder for a falsy value. */
const ALL = "all";

/** Distinct `id → name` pairs found in the events, in their order of appearance. */
const collectFilterOptions = (
  events: EventData[] | undefined,
  getId: (event: EventData) => string | undefined,
  getName: (event: EventData) => string | undefined,
) => {
  const options = new Map<string, string>();
  events?.forEach((event) => {
    const id = getId(event);
    if (id && id !== ALL && !options.has(id)) options.set(id, getName(event) || id);
  });
  return options;
};

const AllSortValues = Object.keys(SortMethods);

const EventItem = ({ data, mode = "class", extraAction, onJoin }: {
  data: EventData,
  mode?: AgendaMode,
  /** Rendered before the main button, in the same slot. */
  extraAction?: React.ReactNode,
  onJoin: () => void,
}) => {
  const [status, setStatus] = useState<EventStatus>(() => getEventStatus(data));
  const [settings] = useSettings();
  const showRoleBadge = !!settings?.ui?.showExpectedRoleBadge;
  const mobile = useAppStoreSelector(state => state.isMobile);

  useEffect(() => {
    // An explicit status does not drift with time, so there is nothing to poll.
    if (data.status) {
      setStatus(data.status);
      return;
    }
    const interval = setInterval(() => {
      setStatus(getEventStatus(data));
    }, 5000);
    return () => clearInterval(interval);
  }, [data]);

  const renderStatus = () => {
    return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
      {showRoleBadge && data.roleInfo?.spectator && <RoleBadge variant="spectator" />}
      {showRoleBadge && data.roleInfo?.expert && <RoleBadge variant="expert" />}
      {/*
        * A state-derived label carries its own styling — neutral, and white while the meeting runs
        * (see `.statusLive`). The time-window highlight only applies to the default wording below,
        * which is all a row has left when the meeting state is unknown.
        */}
      <p style={{ color: !data.statusLabel && status === "ongoing" ? 'var(--color-element-calendar-item-status-ongoing)' : 'var(--color-element-calendar-item-status)' }}>
        {data.statusLabel ?? `Évènement ${status === "incoming" ? "à venir" : status === "ongoing" ? "en cours" : "terminé"}`}
      </p>
    </div>
  );
  };

  // Every row keeps its action: a class that is not running is still joinable, just muted.
  const muted = mode === "class" && status !== "ongoing";

  return (
    <InfoRow
      date={data.startDatetime}
      endDate={data.endDatetime}
      header={data.header ?? ([data.subOrganizationName, data.roomName].filter(Boolean).join(" · ") || undefined)}
      title={data.title}
      small={mobile}
      leftIcon={<ReactIcons.Clock2 width="0.75em" color={"var(--color-agenda-title)"} />}
      rightContent={renderStatus()}
      customButton={
        <div className={styles.actions}>
          {extraAction}
          <PrimaryButton
            onClick={onJoin}
            className={clsx({
              [styles.joinBtn]: true,
              // The same muted variant as a replay: nothing is live to join.
              [styles.replay]: mode === "replay" || (mode === "instant" && status !== "ongoing"),
              [styles.muted]: muted,
            })}
          >
            {data.actionLabel ?? (mode === "replay" ? (
              <>
                <ReactIcons.PlayScreens className={styles.joinBtnIcon} />
                Visionner
              </>
            ) : "Rejoindre")}
          </PrimaryButton>
        </div>
      }
    />
  );
};

export interface AgendaPanelProps {
  /** `undefined` renders placeholders: the data is still loading. */
  events?: EventData[];
  placeholdersNb: number;
  mode?: AgendaMode;
  subtitle?: string;
  emptyLabel?: string;
  /** Sort entries offered in the dropdown. Defaults to date / name / room. */
  sortMethods?: string[];
  onJoinEvent?: (eventId: string) => void;
  /** Extra action rendered on every row, next to the main button. */
  renderExtraAction?: (eventId: string) => React.ReactNode;
}

const DEFAULT_SUBTITLES: { [key in AgendaMode]: string } = {
  class: "Activités",
  replay: "Replays",
  instant: "Instantanés",
};

/**
 * The agenda panel itself: the glass container, the sort dropdown, the rows and their
 * placeholders. Shared by every list tab so they read alike — feed it `EventData`, whatever the
 * underlying document is (a TimeEvent, a meeting, ...).
 */
export const AgendaPanel = ({
  events,
  placeholdersNb,
  mode = "class",
  subtitle,
  emptyLabel = "Aucun évènement trouvé",
  sortMethods = AllSortValues,
  onJoinEvent,
  renderExtraAction,
}: AgendaPanelProps) => {
  const [sortMethod, setSortMethod] = useState<string>(sortMethods[0]);
  const [subOrganizationFilter, setSubOrganizationFilter] = useState<string>(ALL);
  const [roomFilter, setRoomFilter] = useState<string>(ALL);
  const isMobile = useAppStoreSelector(state => state.isMobile);

  // The options come from the loaded events, so a filter only shows when it can split them.
  const subOrganizations = useMemo(
    () => collectFilterOptions(events, (e) => e.subOrganizationId, (e) => e.subOrganizationName),
    [events],
  );
  // A value the events no longer carry (another day, a reload) falls back to "all".
  const activeSubOrganization = subOrganizations.has(subOrganizationFilter) ? subOrganizationFilter : ALL;
  const subOrganizationSelected = activeSubOrganization !== ALL;
  // Rooms narrow to the selected sub-organization.
  const rooms = useMemo(
    () => collectFilterOptions(
      subOrganizationSelected ? events?.filter((e) => e.subOrganizationId === activeSubOrganization) : events,
      (e) => e.roomId,
      // The sub-organization prefix tells apart same-named rooms; once one is selected it is redundant.
      (e) => (subOrganizationSelected ? e.roomName : [e.subOrganizationName, e.roomName].filter(Boolean).join(" · ")),
    ),
    [events, activeSubOrganization, subOrganizationSelected],
  );
  const activeRoom = rooms.has(roomFilter) ? roomFilter : ALL;

  const filteredEvents = useMemo(() => events?.filter((event) => (
    (!subOrganizationSelected || event.subOrganizationId === activeSubOrganization)
    && (activeRoom === ALL || event.roomId === activeRoom)
  )), [events, activeSubOrganization, subOrganizationSelected, activeRoom]);

  const getDropdownLabel = useCallback((value: string) => {
    // Todo : translate
    return SortMethods[value] || "Unknown";
  }, []);

  const sortedEventsData = useMemo<EventData[] | undefined>(() => {
    if (!filteredEvents) return undefined;
    return [...filteredEvents].sort((a, b) => {
      switch (sortMethod) {
        case "date": {
          // Replays and past meetings read backwards: the most recent one first.
          const diff = a.startDatetime.getTime() - b.startDatetime.getTime();
          return mode === "class" ? diff : -diff;
        }
        case "name":
          return a.title.localeCompare(b.title);
        case "room":
          return (a.roomName || "").localeCompare(b.roomName || "");
        default:
          return 0;
      }
    });
  }, [filteredEvents, sortMethod, mode]);

  const eventNodes = useMemo(() => {
    if (!sortedEventsData) return Array.from({ length: placeholdersNb }).map((_, index) => (<InfoRow.Placeholder key={index} />));
    if (sortedEventsData.length === 0) {
      return [<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>{emptyLabel}</div>]
    }
    return sortedEventsData.map((event) => (
      <EventItem
        key={event.id}
        data={event}
        mode={mode}
        extraAction={renderExtraAction?.(event.id)}
        onJoin={() => onJoinEvent?.(event.id)}
      />
    ));
  }, [sortedEventsData, placeholdersNb, emptyLabel, mode, onJoinEvent, renderExtraAction]);

  const filtersNode = subOrganizations.size > 1 || rooms.size > 1 ? (
    <>
      {subOrganizations.size > 1 ? (
        <Dropdown
          width="auto"
          options={[ALL, ...subOrganizations.keys()]}
          selected={activeSubOrganization}
          getLabel={(id) => (id === ALL ? "Toutes les sous-organisations" : subOrganizations.get(id) ?? id)}
          onSelect={setSubOrganizationFilter}
        />
      ) : null}
      {rooms.size > 1 ? (
        <Dropdown
          width="auto"
          options={[ALL, ...rooms.keys()]}
          selected={activeRoom}
          getLabel={(id) => (id === ALL ? "Toutes les salles" : rooms.get(id) ?? id)}
          onSelect={setRoomFilter}
        />
      ) : null}
    </>
  ) : null;

  return (
    <GenericPanelWithDropdown
      className={clsx(styles.container, { [styles.mobile]: isMobile })}
      items={eventNodes}
      headerActions={filtersNode}
      mainTitle="AGENDA"
      subtitle={subtitle ?? DEFAULT_SUBTITLES[mode]}
      // Counted before filtering, so narrowing the list does not hide (and reset) the sort.
      dropdownOptions={(events?.length ?? 0) > 1 ? sortMethods : undefined}
      dropdownWidth="auto"
      getDropdownLabel={getDropdownLabel}
      onSelectOption={setSortMethod}
    />
  );
};

export const TimeEventsAgenda = ({ timeEvents, placeholdersNb, mode, onJoinEvent, user }: TimeEventsAgendaProps) => {
  const eventsData = useMemo<EventData[] | undefined>(() => {
    if (!timeEvents) return undefined;
    const _timeEvents = isArray(timeEvents) ? timeEvents : [timeEvents];
    const eventsData: EventData[] = [];
    _timeEvents.forEach((timeEvent) => {
      const eventData = convertTimeEvent(timeEvent, user);
      if (eventData) eventsData.push(eventData);
    });
    return eventsData;
  }, [timeEvents, user]);

  // Single-event mode: a missing target means the event itself does not resolve.
  if (eventsData && !isArray(timeEvents) && !eventsData[0]) {
    return (
      <div style={{ width: '100%', borderRadius: '15px', height: '200px', backgroundColor: 'rgb(102 102 102)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'lightgrey'}}>
        L'event ne semble pas exister
      </div>
    );
  }

  return (
    <AgendaPanel
      events={eventsData}
      placeholdersNb={placeholdersNb}
      mode={mode}
      onJoinEvent={onJoinEvent}
    />
  );
};
